import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Apple, Dumbbell, HeartPulse, FlaskConical, Moon, Sparkles, Trophy, Info, Brain,
} from 'lucide-react';
import { NANOUK_IMAGES } from '../lib/nanouk-assets';
import {
    Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maps the icon string from the backend to a Lucide component.
const ICONS = {
    apple: Apple,
    dumbbell: Dumbbell,
    heart: HeartPulse,
    lab: FlaskConical,
    moon: Moon,
    sparkles: Sparkles,
    trophy: Trophy,
};

// Pairs the color string from the backend with Tailwind classes.
// Kept inline (not generated) so Tailwind's JIT picks them up.
const COLOR_CLASSES = {
    green: {
        wrap: 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 hover:border-green-400',
        icon: 'text-green-600',
        label: 'text-green-800 dark:text-green-200',
        sub: 'text-green-600/70',
    },
    orange: {
        wrap: 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800 hover:border-orange-400',
        icon: 'text-orange-600',
        label: 'text-orange-800 dark:text-orange-200',
        sub: 'text-orange-600/70',
    },
    rose: {
        wrap: 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800 hover:border-rose-400',
        icon: 'text-rose-600',
        label: 'text-rose-800 dark:text-rose-200',
        sub: 'text-rose-600/70',
    },
    violet: {
        wrap: 'bg-violet-50 dark:bg-violet-950/20 border-violet-200 dark:border-violet-800 hover:border-violet-400',
        icon: 'text-violet-600',
        label: 'text-violet-800 dark:text-violet-200',
        sub: 'text-violet-600/70',
    },
    indigo: {
        wrap: 'bg-indigo-50 dark:bg-indigo-950/20 border-indigo-200 dark:border-indigo-800 hover:border-indigo-400',
        icon: 'text-indigo-600',
        label: 'text-indigo-800 dark:text-indigo-200',
        sub: 'text-indigo-600/70',
    },
    amber: {
        wrap: 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 hover:border-amber-400',
        icon: 'text-amber-600',
        label: 'text-amber-800 dark:text-amber-200',
        sub: 'text-amber-600/70',
    },
};

// Static fallback used while loading or when the endpoint fails.
function getFallback(isDe) {
    return [
        {
            id: 'nutrition', icon: 'apple', color: 'green',
            label: isDe ? 'Ernährung' : 'Nutrition',
            sub: isDe ? 'Tipps & Plan' : 'Tips & Plan',
            query: isDe
                ? 'Gib mir personalisierte Ernährungsempfehlungen basierend auf meinem Profil und meinen heutigen Einträgen.'
                : 'Give me personalized nutrition recommendations.',
        },
        {
            id: 'fitness', icon: 'dumbbell', color: 'orange',
            label: 'Fitness',
            sub: isDe ? 'Training & Plan' : 'Workout Plan',
            query: isDe
                ? 'Erstelle mir einen personalisierten Trainingsplan basierend auf meinem Fitnesslevel und meinen letzten Workouts.'
                : 'Create a personalized training plan based on my fitness level.',
        },
        {
            id: 'health', icon: 'heart', color: 'rose',
            label: isDe ? 'Gesundheit' : 'Health',
            sub: isDe ? 'Check & Analyse' : 'Check & Analyze',
            query: isDe
                ? 'Analysiere meine Gesundheitsdaten und Symptome der letzten Wochen. Gibt es Muster oder Empfehlungen?'
                : 'Analyze my health data and symptoms. Any patterns?',
        },
    ];
}

/**
 * Empty-state for the Coach chat. Now fetches personalized prompts from
 * /api/coach/quick-prompts based on the user's latest lab markers, recent
 * sleep, and quiz-answer goal. Falls back to static prompts on error so
 * the empty state always renders something useful.
 */
export function CoachQuickPrompts({ language, motivation, onAskNanouk }) {
    const isDe = language === 'de';
    const [prompts, setPrompts] = useState(() => getFallback(isDe));
    const [personalized, setPersonalized] = useState(false);
    const [streak, setStreak] = useState(0);
    const [streakTarget, setStreakTarget] = useState(7);
    const [badgeUnlocked, setBadgeUnlocked] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [promptsRes, streakRes] = await Promise.all([
                    axios.get(`${API}/coach/quick-prompts?lang=${isDe ? 'de' : 'en'}`),
                    axios.get(`${API}/coach/insight-streak`).catch(() => null),
                ]);
                if (cancelled) return;
                if (Array.isArray(promptsRes.data?.prompts) && promptsRes.data.prompts.length > 0) {
                    setPrompts(promptsRes.data.prompts);
                    setPersonalized(!!promptsRes.data.personalized);
                }
                if (streakRes?.data) {
                    setStreak(streakRes.data.streak || 0);
                    setStreakTarget(streakRes.data.streak_target || 7);
                    setBadgeUnlocked(!!streakRes.data.badge_unlocked);
                }
            } catch (_e) { /* keep static fallback */ }
        })();
        return () => { cancelled = true; };
    }, [isDe]);

    // Fire when a "Why this prompt?" tooltip is opened. Idempotent per-day-per-prompt.
    const logInsightView = async (promptObj) => {
        if (!promptObj?.reason) return;
        try {
            const res = await axios.post(
                `${API}/coach/insight-view`,
                { source: promptObj.source || 'static', prompt_id: promptObj.id },
                {},
            );
            const data = res.data || {};
            setStreak(data.streak || 0);
            if (data.badge_unlocked) setBadgeUnlocked(true);
            if (data.badge_just_unlocked) {
                toast.success(
                    isDe
                        ? '🧠 Buddy-Brain freigeschaltet! +50 XP'
                        : '🧠 Buddy-Brain unlocked! +50 XP',
                    { duration: 6000 },
                );
            }
        } catch (_e) { /* silent — non-critical */ }
    };

    return (
        <div className="shrink-0 space-y-2" data-testid="coach-quick-prompts">
            {motivation?.motivation && (
                <div className="p-3 rounded-xl bg-muted/50 border border-border">
                    <div className="flex items-start gap-3">
                        <img
                            src={NANOUK_IMAGES.icon}
                            alt="Nanouk"
                            className="w-8 h-8 rounded-lg object-cover shrink-0"
                        />
                        <p className="text-sm leading-relaxed">{motivation.motivation}</p>
                    </div>
                </div>
            )}

            {personalized && (
                <div className="flex items-center justify-between gap-2 px-1" data-testid="coach-prompts-personalized-badge">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        {isDe ? 'Für dich heute' : 'For you today'}
                    </p>
                    {streak > 0 && (
                        <p
                            className={`text-[10px] font-bold flex items-center gap-1 ${badgeUnlocked ? 'text-amber-600 dark:text-amber-400' : 'text-violet-600 dark:text-violet-400'}`}
                            data-testid="insight-streak-chip"
                        >
                            <Brain className="w-3 h-3" />
                            {badgeUnlocked
                                ? (isDe ? `🧠 Buddy-Brain · ${streak}d` : `🧠 Buddy-Brain · ${streak}d`)
                                : `${streak}/${streakTarget}d`}
                        </p>
                    )}
                </div>
            )}

            <div className="mb-3 grid grid-cols-3 gap-2">
                {prompts.map((p) => {
                    const Icon = ICONS[p.icon] || Apple;
                    const c = COLOR_CLASSES[p.color] || COLOR_CLASSES.green;
                    return (
                        <div key={p.id} className="relative">
                            <button
                                type="button"
                                onClick={() => onAskNanouk(p.query)}
                                className={`w-full h-full flex flex-col items-center gap-1 p-3 rounded-xl border transition-colors ${c.wrap}`}
                                data-testid={`coach-prompt-${p.id}`}
                            >
                                <Icon className={`w-5 h-5 ${c.icon}`} />
                                <span className={`text-[11px] font-semibold ${c.label} text-center line-clamp-2`}>{p.label}</span>
                                <span className={`text-[9px] ${c.sub} text-center line-clamp-2`}>{p.sub}</span>
                            </button>
                            {p.reason && (
                                <Popover onOpenChange={(open) => { if (open) logInsightView(p); }}>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            className={`absolute top-1 right-1 w-5 h-5 rounded-full bg-background/70 hover:bg-background border border-border flex items-center justify-center transition-colors ${c.icon}`}
                                            aria-label={isDe ? 'Warum dieser Vorschlag?' : 'Why this prompt?'}
                                            data-testid={`coach-prompt-${p.id}-why`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Info className="w-3 h-3" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        side="top"
                                        align="end"
                                        className="w-64 text-xs leading-relaxed p-3"
                                        data-testid={`coach-prompt-${p.id}-reason`}
                                    >
                                        <p className="font-semibold mb-1 flex items-center gap-1">
                                            <Sparkles className={`w-3 h-3 ${c.icon}`} />
                                            {isDe ? 'Warum dieser Vorschlag?' : 'Why this prompt?'}
                                        </p>
                                        <p className="text-muted-foreground">{p.reason}</p>
                                    </PopoverContent>
                                </Popover>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
